
import React from 'react';
import { motion } from 'framer-motion';
import { Book, GraduationCap, Users } from 'lucide-react';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";

export function FounderSection() {
  return (
    <section className="py-20 bg-gradient-to-b from-white to-indigo-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Heading */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              👋 Meet Nicole – Your Guide to Smarter Sleep Consulting
            </h2>
            <p className="text-lg text-gray-600">
              The expert behind SleepNest's Sleep Training Library
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-10 items-center">
            {/* Image and Bio */}
            <div className="lg:w-2/5">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative"
              >
                <div className="absolute -top-3 -left-3 w-full h-full bg-indigo-100 rounded-lg transform rotate-[-2deg] z-0"></div>
                <div className="relative z-10 rounded-lg overflow-hidden shadow-lg border-4 border-white">
                  <img 
                    src="https://gbmarketplacestorage.blob.core.windows.net/sleepnest/FounderTwo.jpg" 
                    alt="Nicole Morales, Founder of SleepNest" 
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div className="absolute -bottom-3 -right-3 w-32 h-32 bg-indigo-50 rounded-full -z-10 opacity-80"></div>
              </motion.div>
            </div>

            {/* Bio and Contributions */}
            <div className="lg:w-3/5">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Card className="bg-white/80 backdrop-blur-sm border border-indigo-100 shadow-md">
                  <CardHeader>
                    <CardTitle className="text-2xl font-semibold text-indigo-800">About Nicole</CardTitle>
                    <CardDescription className="text-gray-700 text-base">
                      Nicole is a seasoned pediatric sleep expert, postpartum doula, and newborn care specialist. With years of experience supporting families and training sleep consultants, she's here to make your work easier and more impactful.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <h3 className="text-xl font-semibold text-indigo-700 mb-4">
                      🌟 Nicole's Role in SleepNest
                    </h3>
                    
                    <div className="space-y-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-indigo-100 p-2 rounded-full">
                          <Book className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">📖 Creator of the Sleep Training Library</h4>
                          <p className="text-gray-700">
                            Developed expert-designed sleep plans for specialists to use with their clients.
                          </p>
                        </div>
                      </div>
                      
                      <Separator className="bg-indigo-100" />
                      
                      <div className="flex items-start gap-4">
                        <div className="bg-indigo-100 p-2 rounded-full">
                          <GraduationCap className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">🎓 Educator & Mentor</h4>
                          <p className="text-gray-700">
                            Provides practical insights and guidance to help specialists level up their practice.
                          </p>
                        </div>
                      </div>
                      
                      <Separator className="bg-indigo-100" />
                      
                      <div className="flex items-start gap-4">
                        <div className="bg-indigo-100 p-2 rounded-full">
                          <Users className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">🤝 Community Builder</h4>
                          <p className="text-gray-700">
                            Helps foster collaboration among sleep consultants inside SleepNest.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}